import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import type { WizardStepContext } from "@/base";
import { messageFor } from "@/lib/errors";
import { getProducts } from "@/lib/brokerProducts";
import { soleWcProduct, type WcDraft } from "@/lib/submissions/wc/draft";
import { answersFrom } from "@/lib/submissions/wc/questions";

/** What a catalog without the WC product's manifest says. */
export const WC_MANIFEST_ABSENT =
  "Workers’ Comp’s questions aren’t in the catalog.";

/**
 * Reads the WC product's manifest when the draft has none: a new application,
 * before it exists (BRK-S388), from the catalog's one Workers' Comp product,
 * or a saved one whose load couldn't read it (BRK-S304). A failed read stays
 * the page's to retry rather than failing the whole application. The read
 * goes through `setLoaded`, so it is never an unsaved change (BRK-S307).
 */
export function useWcManifest(
  draft: WcDraft,
  setLoaded: WizardStepContext<WcDraft>["setLoaded"],
): { problem: string | null; retry: () => void } {
  const { authedRequest } = useAuth();
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const productId = draft.submission?.product?.id;
  const needed = draft.manifest === null;

  useEffect(() => {
    if (!needed) return;
    let cancelled = false;
    setProblem(null);
    getProducts(authedRequest).then(
      ({ items }) => {
        if (cancelled) return;
        const manifest = (
          productId
            ? items.find((p) => p.id === productId)
            : soleWcProduct(items)
        )?.manifest;
        if (!manifest) {
          setProblem(WC_MANIFEST_ABSENT);
          return;
        }
        setLoaded((d) => ({
          ...d,
          manifest,
          answers: answersFrom(manifest, d.submission?.data ?? {}),
        }));
      },
      (err: unknown) => {
        if (!cancelled) setProblem(messageFor(err));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [needed, productId, attempt, authedRequest, setLoaded]);

  return { problem, retry: () => setAttempt((n) => n + 1) };
}
